import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    RadarChart,
    Radar,
    ScatterChart,
    Scatter,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { Button, Box, Collapse, Typography, Grid, Card } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Generate random colors for pie/radar charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Custom shape components for larger scatter points
const renderLargeCircle = (props) => {
    const { cx, cy, fill, payload, ...rest } = props;
    // Use green for target brand (first brand), blue for others
    const circleColor = payload.isTargetBrand ? "#22c55e" : fill;

    return (
        <circle
            cx={cx}
            cy={cy}
            r={6}
            fill={circleColor}
            stroke={circleColor}
            strokeWidth={2}
            style={{ cursor: 'pointer' }}
            {...rest}
        />
    );
};

const renderLargeTriangle = (props) => {
    const { cx, cy, fill, payload, ...rest } = props;
    const size = 6;
    const path = `M ${cx},${cy - size} L ${cx + size},${cy + size} L ${cx - size},${cy + size} Z`;

    return (
        <g>
            <path
                d={path}
                fill={fill}
                stroke={fill}
                strokeWidth={2}
                style={{ cursor: 'pointer' }}
                {...rest}
            />
            {/* Show label only if showSegmentLabels is true and label exists */}
            {payload.label && payload.showSegmentLabels && (
                <text
                    x={cx}
                    y={cy - 20}
                    textAnchor="middle"
                    fill="#333"
                    fontSize="12px"
                    fontWeight="normal"
                >
                    {payload.label}
                </text>
            )}
        </g>
    );
};

const ChartRenderer = ({ visualization }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [expandedLevel, setExpandedLevel] = useState(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!visualization) return null;

    const { type, data } = visualization;
    const categoryThreshold = isMobile ? 5 : 8;

    // Common chart configuration
    const commonProps = {
        width: "100%",
        height: 300,
        margin: { top: 20, right: 30, left: 20, bottom: 70 }
    };

    switch (type) {
        case 'bar':
            // Handle multiple datasets
            let chartData = data.labels.map((label, i) => {
                const dataPoint = { name: label };
                data.datasets.forEach((dataset, datasetIndex) => {
                    const dataKey = dataset.label || `value${datasetIndex}`;
                    // Ensure data value is a number, not an object
                    let value = dataset.data[i];
                    if (typeof value === 'object' && value !== null) {
                        // If it's an object, try to extract a numeric value
                        value = value.value || value.data || value.percentage || 0;
                    }
                    dataPoint[dataKey] = parseFloat(value) || 0;
                    // Store sample size for tooltip
                    if (dataset.sampleSizes?.[i]) {
                        dataPoint[`${dataKey}_sampleSize`] = dataset.sampleSizes[i];
                    }
                });
                return dataPoint;
            });

            const highlightOverall = visualization.options?.highlightOverall;
            const highlightSegmentAverage = visualization.options?.highlightSegmentAverage;
            const highlightTargetBrand = visualization.options?.highlightTargetBrand;
            const targetBrand = visualization.options?.targetBrand;
            const hideSampleSizes = visualization.options?.hideSampleSizes;

            // Sort data for segment level comparison chart (keep segment average first, then sort rest)
            if (highlightSegmentAverage && highlightTargetBrand) {
                // Separate first item (segment average) from the rest
                const firstItem = chartData[0];
                const restItems = chartData.slice(1);

                // Sort the rest by the first dataset's values in descending order
                const dataKey = data.datasets[0]?.label || 'value0';
                restItems.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));

                // Reconstruct array with segment average first, then sorted items
                chartData = [firstItem, ...restItems];
            }

            // Colors for multiple datasets
            const barColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];

            return (
                <>
                <ResponsiveContainer {...commonProps}>
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="name"
                            interval={0}
                            angle={data.labels.length >= categoryThreshold ? -45 : 0}
                            textAnchor={data.labels.length >= categoryThreshold ? "end" : "middle"}
                            height={60}
                            tick={{
                                fontSize: 12,
                                width: 100,
                                wordWrap: 'break-word'
                            }}
                        />
                        <YAxis />
                        <Tooltip
                            formatter={(value, name, props) => {
                                // Check if sample sizes should be hidden
                                if (hideSampleSizes) {
                                    return [`${value}%`, name];
                                }
                                const sampleSizeKey = `${name}_sampleSize`;
                                const sampleSize = props.payload[sampleSizeKey];
                                if (sampleSize) {
                                    return [`${value}% (n=${sampleSize})`, name];
                                }
                                return [`${value}%`, name];
                            }}
                        />
                        <Legend />
                        {data.datasets.map((dataset, datasetIndex) => {
                            const dataKey = dataset.label || `value${datasetIndex}`;

                            // For single dataset with highlighting
                            if (data.datasets.length === 1 && (highlightOverall || highlightSegmentAverage || highlightTargetBrand)) {
                                // Debug logging for target brand highlighting
                                if (highlightTargetBrand) {
                                    console.log('Target Brand Highlighting Debug:');
                                    console.log('  highlightTargetBrand:', highlightTargetBrand);
                                    console.log('  targetBrand:', targetBrand);
                                    console.log('  targetBrand type:', typeof targetBrand);
                                    console.log('  Chart labels:', data.labels);
                                    console.log('  Chart data entries:', chartData.map(e => ({ name: e.name, type: typeof e.name })));
                                }

                                return (
                                    <Bar
                                        key={datasetIndex}
                                        dataKey={dataKey}
                                        name={dataset.label}
                                    >
                                        {chartData.map((entry, index) => {
                                            let fillColor = "#8884d8"; // Default color

                                            if ((highlightOverall && index === 0) || (highlightSegmentAverage && index === 0)) {
                                                fillColor = "#FF6B35"; // Highlight color for first bar (Overall/Segment Average)
                                            } else if (highlightTargetBrand && targetBrand && entry.name === targetBrand) {
                                                console.log(`  Match found! entry.name "${entry.name}" === targetBrand "${targetBrand}"`);
                                                fillColor = "#4CAF50"; // Green color for target brand
                                            } else if (highlightTargetBrand && targetBrand) {
                                                console.log(`  No match: entry.name "${entry.name}" !== targetBrand "${targetBrand}"`);
                                            }

                                            return (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={fillColor}
                                                />
                                            );
                                        })}
                                    </Bar>
                                );
                            }

                            // For multiple datasets or no highlighting
                            return (
                                <Bar
                                    key={datasetIndex}
                                    dataKey={dataKey}
                                    name={dataset.label}
                                    fill={barColors[datasetIndex % barColors.length]}
                                />
                            );
                        })}
                    </BarChart>
                </ResponsiveContainer>
                {visualization.note && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                        {visualization.note}
                    </Typography>
                )}
                </>
            );

        case 'horizontal_bar': {
            // Horizontal bar chart — brands on Y-axis, values on X-axis
            // Avoids crowding/overlap of brand names regardless of count or name length
            let hBarData = data.labels.map((label, i) => {
                const dataPoint = { name: label };
                data.datasets.forEach((dataset, datasetIndex) => {
                    const dataKey = dataset.label || `value${datasetIndex}`;
                    let value = dataset.data[i];
                    if (typeof value === 'object' && value !== null) {
                        value = value.value || value.data || value.percentage || 0;
                    }
                    dataPoint[dataKey] = parseFloat(value) || 0;
                    if (dataset.sampleSizes?.[i]) {
                        dataPoint[`${dataKey}_sampleSize`] = dataset.sampleSizes[i];
                    }
                });
                return dataPoint;
            });

            const hHighlightOverall = visualization.options?.highlightOverall;
            const hHighlightSegmentAverage = visualization.options?.highlightSegmentAverage;
            const hHighlightTargetBrand = visualization.options?.highlightTargetBrand;
            const hTargetBrand = visualization.options?.targetBrand;
            const hHideSampleSizes = visualization.options?.hideSampleSizes;

            // Sort: keep segment average first, sort rest descending
            if (hHighlightSegmentAverage && hHighlightTargetBrand) {
                const firstItem = hBarData[0];
                const restItems = hBarData.slice(1);
                const dataKey = data.datasets[0]?.label || 'value0';
                restItems.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));
                hBarData = [firstItem, ...restItems];
            }

            // Width for the Y-axis label area — scale with longest label
            const maxLabelLen = Math.max(...data.labels.map(l => String(l).length));
            const yAxisWidth = Math.min(Math.max(maxLabelLen * 7, 100), 220);

            const hBarColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];
            const itemHeight = 40;

            // The first bar (segment/overall average) is highlighted by NAME rather
            // than by index, so it keeps its colour even after the chart is split.
            const firstBarName = (hHighlightOverall || hHighlightSegmentAverage)
                ? hBarData[0]?.name
                : null;
            const useHighlightColors = data.datasets.length === 1
                && (hHighlightOverall || hHighlightSegmentAverage || hHighlightTargetBrand);

            // Shared X-axis domain so bar lengths stay comparable across split charts.
            const primaryKey = data.datasets[0]?.label || 'value0';
            const hXMax = Math.max(0, ...hBarData.map(r => r[primaryKey] || 0));
            const hXDomain = [0, Math.max(10, Math.ceil(hXMax / 10) * 10)];

            // Render a single horizontal bar chart for a subset of rows.
            const renderHBar = (rows, keyPrefix, showLegend) => {
                const height = Math.max(220, rows.length * itemHeight + 60);
                return (
                    <ResponsiveContainer key={keyPrefix} width="100%" height={height}>
                        <BarChart
                            data={rows}
                            layout="vertical"
                            margin={{ top: 10, right: 60, left: 10, bottom: 10 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                type="number"
                                domain={hXDomain}
                                tickFormatter={(v) => `${v}%`}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={yAxisWidth}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                                formatter={(value, name, props) => {
                                    if (hHideSampleSizes) {
                                        return [`${value}%`, name];
                                    }
                                    const sampleSizeKey = `${name}_sampleSize`;
                                    const sampleSize = props.payload[sampleSizeKey];
                                    if (sampleSize) {
                                        return [`${value}% (n=${sampleSize})`, name];
                                    }
                                    return [`${value}%`, name];
                                }}
                            />
                            {showLegend && <Legend />}
                            {data.datasets.map((dataset, datasetIndex) => {
                                const dataKey = dataset.label || `value${datasetIndex}`;

                                if (useHighlightColors) {
                                    return (
                                        <Bar key={datasetIndex} dataKey={dataKey} name={dataset.label}>
                                            {rows.map((entry, index) => {
                                                let fillColor = '#8884d8';
                                                if (firstBarName && entry.name === firstBarName) {
                                                    fillColor = '#FF6B35';
                                                } else if (hHighlightTargetBrand && hTargetBrand && entry.name === hTargetBrand) {
                                                    fillColor = '#4CAF50';
                                                }
                                                return <Cell key={`cell-${index}`} fill={fillColor} />;
                                            })}
                                        </Bar>
                                    );
                                }

                                return (
                                    <Bar
                                        key={datasetIndex}
                                        dataKey={dataKey}
                                        name={dataset.label}
                                        fill={hBarColors[datasetIndex % hBarColors.length]}
                                    />
                                );
                            })}
                        </BarChart>
                    </ResponsiveContainer>
                );
            };

            // When a segment has many category leaders, a single chart gets very
            // tall and hard to read. Split it into two stacked charts (top half /
            // bottom half by trust share) that share the same X-axis scale, so no
            // data is dropped and bars stay comparable.
            const hBarNote = visualization.note && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    {visualization.note}
                </Typography>
            );

            const SPLIT_THRESHOLD = 16;
            if (useHighlightColors && hBarData.length > SPLIT_THRESHOLD) {
                const mid = Math.ceil(hBarData.length / 2);
                const firstHalf = hBarData.slice(0, mid);
                const secondHalf = hBarData.slice(mid);
                return (
                    <Box>
                        {renderHBar(firstHalf, 'hbar-top', true)}
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', my: 1 }}
                        >
                            continued — lower trust share ↓
                        </Typography>
                        {renderHBar(secondHalf, 'hbar-bottom', false)}
                        {hBarNote}
                    </Box>
                );
            }

            return (
                <Box>
                    {renderHBar(hBarData, 'hbar', true)}
                    {hBarNote}
                </Box>
            );
        }

        case 'line':
            // Handle multiple datasets for line chart
            const lineData = data.labels.map((label, i) => {
                const dataPoint = { name: label };
                data.datasets.forEach((dataset, datasetIndex) => {
                    const dataKey = dataset.label || `value${datasetIndex}`;
                    dataPoint[dataKey] = dataset.data[i];
                    // Store sample size for tooltip
                    if (dataset.sampleSizes?.[i]) {
                        dataPoint[`${dataKey}_sampleSize`] = dataset.sampleSizes[i];
                    }
                });
                return dataPoint;
            });

            const lineColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];

            return (
                <ResponsiveContainer {...commonProps}>
                    <LineChart data={lineData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                            formatter={(value, name, props) => {
                                const sampleSizeKey = `${name}_sampleSize`;
                                const sampleSize = props.payload[sampleSizeKey];
                                if (sampleSize) {
                                    return [`${value}% (n=${sampleSize})`, name];
                                }
                                return [`${value}%`, name];
                            }}
                        />
                        <Legend />
                        {data.datasets.map((dataset, datasetIndex) => {
                            const dataKey = dataset.label || `value${datasetIndex}`;
                            return (
                                <Line
                                    key={datasetIndex}
                                    type="monotone"
                                    dataKey={dataKey}
                                    stroke={lineColors[datasetIndex % lineColors.length]}
                                    name={dataset.label}
                                />
                            );
                        })}
                    </LineChart>
                </ResponsiveContainer>
            );

        case 'pie':
            const pieData = data.labels.map((label, i) => ({
                name: label,
                value: data.datasets[0].data[i],
                sampleSize: data.datasets[0].sampleSizes?.[i]
            }));

            return (
                <ResponsiveContainer {...commonProps}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                        >
                            {data.labels.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value, name, props) => {
                                const sampleSize = props.payload.sampleSize;
                                if (sampleSize) {
                                    return [`${value}% (n=${sampleSize})`, name];
                                }
                                return [`${value}%`, name];
                            }}
                        />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            );

        case 'radar':
            // Handle multiple datasets for radar chart
            const radarData = data.labels.map((label, i) => {
                const dataPoint = { subject: label };
                data.datasets.forEach((dataset, datasetIndex) => {
                    const dataKey = dataset.label || `value${datasetIndex}`;
                    dataPoint[dataKey] = dataset.data[i];
                    // Store sample size for tooltip
                    if (dataset.sampleSizes?.[i]) {
                        dataPoint[`${dataKey}_sampleSize`] = dataset.sampleSizes[i];
                    }
                });
                return dataPoint;
            });

            const radarColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];

            return (
                <ResponsiveContainer {...commonProps}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis />
                        {data.datasets.map((dataset, datasetIndex) => {
                            const dataKey = dataset.label || `value${datasetIndex}`;
                            return (
                                <Radar
                                    key={datasetIndex}
                                    name={dataset.label}
                                    dataKey={dataKey}
                                    stroke={radarColors[datasetIndex % radarColors.length]}
                                    fill={radarColors[datasetIndex % radarColors.length]}
                                    fillOpacity={0.2}
                                />
                            );
                        })}
                        <Legend />
                        <Tooltip
                            formatter={(value, name, props) => {
                                const sampleSizeKey = `${name}_sampleSize`;
                                const sampleSize = props.payload[sampleSizeKey];
                                if (sampleSize) {
                                    return [`${value}% (n=${sampleSize})`, name];
                                }
                                return [`${value}%`, name];
                            }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            );

        case 'scatter':
            return (
                <>
                <ResponsiveContainer
                    width="80%"
                    aspect={1}
                >
                    <ScatterChart
                        margin={{ top: 20, right: 30, left: 60, bottom: 40 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            type="number"
                            dataKey="x"
                            name={visualization.options?.scales?.x?.title || 'X Axis'}
                            unit={visualization.options?.scales?.x?.title?.includes('%') ? '%' : ''}
                            domain={['auto', 'auto']}
                            label={{
                                value: visualization.options?.scales?.x?.title || 'X Axis',
                                position: 'insideBottom',
                                offset: -10,
                                style: { fontSize: '16px', fontWeight: 'bold' }
                            }}
                            tick={{ fontSize: 14 }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            name={visualization.options?.scales?.y?.title || 'Y Axis'}
                            unit={visualization.options?.scales?.y?.title?.includes('%') ? '%' : ''}
                            domain={['auto', 'auto']}
                            label={{
                                value: visualization.options?.scales?.y?.title || 'Y Axis',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fontSize: '16px', fontWeight: 'bold' }
                            }}
                            tick={{ fontSize: 14 }}
                        />
                        <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={({ payload }) => {
                                if (!payload || !payload.length) return null;

                                const point = payload[0].payload;

                                // Show segment name for segments, brand name for brands
                                const displayName = point.segmentName || point.brandName || 'Unknown';

                                return (
                                    <div style={{
                                        backgroundColor: 'white',
                                        padding: '8px 12px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}>
                                        <div>{displayName}</div>
                                        {point.categoryName && (
                                            <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
                                                {point.categoryName}
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                        {data.datasets.map((dataset, index) => {
                            const isSegmentDataset = dataset.label === "Segment Averages";
                            const showSegmentLabels = visualization.options?.showSegmentLabels !== false; // Default to true if not specified

                            const dataWithLabels = dataset.data.map((point, pointIndex) => ({
                                ...point,
                                // Add label only for segment averages
                                label: isSegmentDataset ? point.segmentName : undefined,
                                // Pass showSegmentLabels option to control label visibility
                                showSegmentLabels: showSegmentLabels,
                                // Add brand name for tooltip
                                brandName: !isSegmentDataset && data.labels ? data.labels[pointIndex] : undefined,
                                // Check if this brand is marked as target/main brand from backend
                                // Otherwise, don't mark any as target
                                isTargetBrand: !isSegmentDataset && (point.isMainBrand || point.isTargetBrand || false)
                            }));

                            return (
                                <Scatter
                                    key={index}
                                    name={dataset.label}
                                    data={dataWithLabels}
                                    fill={index === 0 ? "#8884d8" : "#ff7300"}
                                    shape={index === 0 ? renderLargeCircle : renderLargeTriangle}
                                />
                            );
                        })}
                    </ScatterChart>
                </ResponsiveContainer>
                {visualization.note && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                        {visualization.note}
                    </Typography>
                )}
                </>
            );

        case 'pyramid':
        case 'hierarchy':
            // Create pyramid visualization for BrandSpark Trust Model
            const maxWidth = visualization.options?.maxWidth || 100;
            const showCompetitorLines = visualization.options?.showCompetitorLines || false;
            const expandable = visualization.options?.expandable || false;
            const attributeDetails = visualization.attribute_details || {};
            
            const pyramidData = data.labels.map((label, i) => ({
                level: label,
                value: data.datasets[0].data[i],
                segmentAverage: data.datasets[1]?.data[i] || 0,
                competitor2: data.datasets[2]?.data[i] || 0,
                competitor3: data.datasets[3]?.data[i] || 0,
                // Calculate width based on hierarchy level but make it 100% max width
                width: maxWidth, // All levels now have 100% width
                color: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0'][i] || '#8884d8'
            }));
            
            return (
                <div style={{ 
                    width: '100%', 
                    maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    boxSizing: 'border-box'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                        {visualization.title}
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        maxWidth: '100%',
                        padding: '0 10px',
                        boxSizing: 'border-box'
                    }}>
                        {pyramidData.reverse().map((level, index) => {
                            const maxThreshold = 100; // 100% is the max scale
                            const innerBarWidth = Math.min(level.value / maxThreshold * 100, 100);
                            const isExpanded = expandedLevel === index;
                            
                            return (
                                <div key={index} style={{ 
                                    width: '100%', 
                                    maxWidth: '100%',
                                    position: 'relative',
                                    boxSizing: 'border-box'
                                }}>
                                    <div
                                        style={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            height: '80px',
                                            backgroundColor: `${level.color}30`,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '0 20px',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            margin: '0 auto',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {/* Inner progress bar */}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                height: '100%',
                                                width: `${innerBarWidth}%`,
                                                backgroundColor: level.color,
                                                borderRadius: '8px',
                                                transition: 'width 0.3s ease',
                                                maxWidth: '100%'
                                            }}
                                        />
                                        
                                        {/* Competitor lines */}
                                        {showCompetitorLines && (
                                            <>
                                                {/* Segment Average Line */}
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${Math.min(level.segmentAverage / maxThreshold * 100, 100)}%`,
                                                        top: 0,
                                                        height: '100%',
                                                        width: '3px',
                                                        backgroundColor: '#FFD700',
                                                        zIndex: 2
                                                    }}
                                                />
                                                {/* 2nd Competitor Line */}
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${Math.min(level.competitor2 / maxThreshold * 100, 100)}%`,
                                                        top: 0,
                                                        height: '100%',
                                                        width: '3px',
                                                        backgroundColor: '#FF4444',
                                                        zIndex: 2
                                                    }}
                                                />
                                                {/* 3rd Competitor Line */}
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${Math.min(level.competitor3 / maxThreshold * 100, 100)}%`,
                                                        top: 0,
                                                        height: '100%',
                                                        width: '3px',
                                                        backgroundColor: '#FF8844',
                                                        zIndex: 2
                                                    }}
                                                />
                                            </>
                                        )}
                                        
                                        {/* Content overlay */}
                                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                                            <span style={{ fontSize: '16px', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>
                                                {level.level}
                                            </span>
                                            {expandable && attributeDetails[level.level] && (
                                                <Button
                                                    onClick={() => setExpandedLevel(isExpanded ? null : index)}
                                                    style={{
                                                        marginLeft: '10px',
                                                        minWidth: '24px',
                                                        padding: '4px',
                                                        color: 'white'
                                                    }}
                                                    size="small"
                                                >
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </Button>
                                            )}
                                        </div>
                                        
                                        <div style={{ textAlign: 'right', position: 'relative', zIndex: 1 }}>
                                            <div style={{ fontSize: '18px', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>
                                                {level.value}%
                                            </div>
                                            {showCompetitorLines && (
                                                <div style={{ fontSize: '11px', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>
                                                    Avg: {level.segmentAverage}%
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Expandable attributes section */}
                                    {expandable && attributeDetails[level.level] && (
                                        <Collapse in={isExpanded}>
                                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {level.level} Attributes:
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {attributeDetails[level.level].map((attribute, attrIndex) => (
                                                        <Grid item xs={12} sm={6} key={attrIndex}>
                                                            <Card variant="outlined" sx={{ p: 1 }}>
                                                                <Typography variant="body2">
                                                                    {attribute}
                                                                </Typography>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        </Collapse>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Legend for competitor lines */}
                    {showCompetitorLines && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <div style={{ width: '20px', height: '3px', backgroundColor: '#FFD700' }} />
                                <Typography variant="caption">Segment Average</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <div style={{ width: '20px', height: '3px', backgroundColor: '#FF4444' }} />
                                <Typography variant="caption">2nd Competitor</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <div style={{ width: '20px', height: '3px', backgroundColor: '#FF8844' }} />
                                <Typography variant="caption">3rd Competitor</Typography>
                            </Box>
                        </Box>
                    )}
                    
                    <div style={{ 
                        textAlign: 'center', 
                        marginTop: '15px', 
                        fontSize: '12px', 
                        color: '#666' 
                    }}>
                        Foundation → Advanced (Build from bottom up)
                    </div>
                </div>
            );

        case 'table':
            return (
                <div style={{ width: '100%', marginBottom: '20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: visualization.description ? '6px' : '15px', fontWeight: 'bold', fontSize: '16px' }}>
                        {visualization.title}
                    </div>
                    {visualization.description && (
                        <div style={{ textAlign: 'center', marginBottom: '15px', fontSize: '13px', color: '#555', fontStyle: 'italic' }}>
                            {visualization.description}
                        </div>
                    )}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                    {data.headers.map((header, index) => (
                                        <th key={index} style={{ 
                                            padding: '12px 8px', 
                                            border: '1px solid #ddd',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            fontSize: '14px'
                                        }}>
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex} style={{ 
                                        backgroundColor: rowIndex % 2 === 0 ? 'white' : '#fafafa' 
                                    }}>
                                        {row.map((cell, cellIndex) => {
                                            // Determine if this cell should be highlighted
                                            let backgroundColor = 'transparent';
                                            let color = '#333';
                                            
                                            if (cellIndex > 0 && cellIndex < row.length - 1) { // Skip first (attribute name) and last (segment average) columns
                                                const values = row.slice(1, -1).map(v => parseFloat(v) || 0);
                                                const currentValue = parseFloat(cell) || 0;
                                                const maxValue = Math.max(...values);
                                                
                                                if (currentValue === maxValue && currentValue > 0) {
                                                    backgroundColor = '#e8f5e8';
                                                    color = '#2e7d32';
                                                } else if (currentValue > 0) {
                                                    backgroundColor = '#fff3e0';
                                                    color = '#f57c00';
                                                }
                                            }
                                            
                                            return (
                                                <td key={cellIndex} style={{ 
                                                    padding: '10px 8px', 
                                                    border: '1px solid #ddd',
                                                    fontSize: '13px',
                                                    backgroundColor,
                                                    color,
                                                    fontWeight: cellIndex === 0 ? 'normal' : 'bold'
                                                }}>
                                                    {cellIndex === 0 ? cell : 
                                                        (typeof cell === 'string' && cell.includes('%') ? cell : `${cell}%`)
                                                    }
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {visualization.note && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                            {visualization.note}
                        </Typography>
                    )}
                </div>
            );

        default:
            return null;
    }
};

export default ChartRenderer;
